import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_ERROR_CODES, PARENT_EVENTS } from '../constants';
import { startDesignLoadTiming, endDesignLoadTiming, TimingEntry } from '../utils/designLoadTiming';

declare module 'axios' {
	interface InternalAxiosRequestConfig<D = any> {
    _retry?: boolean;
    _timingEntry?: TimingEntry | null;
  }
}

const api = axios.create();

const searchParams = new URLSearchParams(window.location.search);
const getExternalID = () => searchParams.get('external-id') ?? '';
const roleKey = (externalID: string) => `roleHash-${externalID}`;
const getStoredRoleHash = (externalID: string) => localStorage.getItem(roleKey(externalID)) ?? undefined;

let refreshPromise: Promise<string | undefined> | null = null;

function requestRoleTokenFromParent(externalID: string): Promise<string | undefined> {
	window.parent.postMessage(
		{ id: API_ERROR_CODES.GET_ROLE_TOKEN, externalID },
		'*'
	);

	return new Promise((resolve) => {
		const onMessage = (event: MessageEvent) => {
			const data = event.data;
			if (!data || data.id !== PARENT_EVENTS.UPDATED_ROLE_TOKEN) return;

			const newToken: string | undefined = data.roleToken;
			if (newToken) localStorage.setItem(roleKey(externalID), newToken);

			window.removeEventListener('message', onMessage);
			resolve(newToken);
		};

		window.addEventListener('message', onMessage);
	});
}

async function refreshRoleToken(externalID: string): Promise<string | undefined> {
	if (!refreshPromise) {
		refreshPromise = (async () => {
			try {
				const token = await requestRoleTokenFromParent(externalID);
				return token ?? getStoredRoleHash(externalID);
			} finally {
				refreshPromise = null;
			}
		})();
	}
	return refreshPromise;
}

api.interceptors.request.use((config) => {
	if (!config.params) config.params = {};
	const externalID = getExternalID();
	config.params.externalID = externalID;
	config.params.roleHash = getStoredRoleHash(externalID);
	config._timingEntry = startDesignLoadTiming(config.url ?? '');
	return config;
});

api.interceptors.response.use(
	(response: AxiosResponse) => {
		const config = response.config as InternalAxiosRequestConfig;
		if (config._timingEntry) {
			endDesignLoadTiming(config._timingEntry, {
				ok: true,
				status: response.status,
				responseData: response.data,
			});
		}
		return response;
	},
	async (error: AxiosError<any>) => {
		const status = error.response?.status;
		const code = (error.response?.data as any)?.code;
		const originalConfigForTiming = error.config as InternalAxiosRequestConfig | undefined;
		if (originalConfigForTiming?._timingEntry) {
			endDesignLoadTiming(originalConfigForTiming._timingEntry, {
				ok: false,
				status,
				errorCode: (error.response?.data as any)?.errorCode ?? (error.response?.data as any)?.error,
				responseData: error.response?.data,
			});
		}

		if (status === 401 && code === API_ERROR_CODES.ROLE_TOKEN_EXPIRED) {
			const originalConfig = error.config as InternalAxiosRequestConfig | undefined;
			console.log('originalConfig: ', originalConfig);
			if (originalConfig && !originalConfig._retry) {
				originalConfig._retry = true;

				const externalID = getExternalID();
				const newToken = await refreshRoleToken(externalID);

				if (!originalConfig.params) originalConfig.params = {};
				originalConfig.params.roleHash = newToken;
				originalConfig.params.externalID = externalID;
				console.log('retrying request with new roleHash: ', originalConfig);
				return api.request(originalConfig);
			}
		}
		return Promise.reject(error);
	},
);

export default api;
